import Close from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { useMoveCommunication } from "../hooks/use-move-communication";
import { moveDestinations, pathText } from "../move-destinations";
import { DestinationList } from "./destination-list";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { TimelineRow } from "../program-timeline";
import type { FC } from "react";

interface MoveProps {
  row: TimelineRow;
  /** The program or folder that it is in now. */
  programId: ProgramNode["id"];
  tree: ProgramTree;
  onClose: () => void;
  /** After it moves, and after it moves back. */
  onMoved: () => void;
}

/** The dialog's content, so that the search and the choice start again each time it opens. */
const MoveForm: FC<MoveProps & ReturnType<typeof useMoveCommunication>> = (props) => {
  const { row, programId, tree, onClose, onMoved, move, moving } = props;
  const [query, setQuery] = useState("");
  const [destination, setDestination] = useState<ProgramNode | null>(null);
  const pendo = usePendo();
  const rows = moveDestinations(tree, programId, query);
  // Only a choice that the search still shows can be moved to.
  const chosen = rows.find(({ node }) => node.id === destination?.id)?.node;

  const submit = async (to: ProgramNode): Promise<void> => {
    if (!(await move(row.id, to.id))) return;
    pendo?.track("program-communication-moved", {
      momentId: row.id,
      fromProgramId: programId,
      toProgramId: to.id,
    });
    onMoved();
    onClose();
    // One toast for each Communication, so that a newer move or the undo replaces it.
    const id = `move-${row.id}`;
    const undo = async (): Promise<void> => {
      if (!(await move(row.id, programId))) return;
      pendo?.track("program-communication-move-undone", { momentId: row.id, programId });
      onMoved();
      toast.success(`Moved “${row.title}” back to ${pathText(tree, programId)}.`, { id });
    };
    // Says where it went, with a button to move it back, for longer than the 2s default.
    toast.success(
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <span>{`Moved “${row.title}” to ${pathText(tree, to.id)}.`}</span>
        <Button
          size="small"
          onClick={() => void undo().catch(console.error)}
          data-analytics-id="program-timeline-move-undo"
        >
          Undo
        </Button>
      </Stack>,
      { id, duration: 10_000 },
    );
  };

  return (
    <>
      <DialogTitle id="move-title" sx={{ pr: 7 }}>
        <Typography
          variant="overline"
          sx={{ display: "block", fontWeight: 700, lineHeight: "20px", color: "text.secondary" }}
        >
          Move communication
        </Typography>
        Move “{row.title}”
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        disabled={moving}
        sx={{ position: "absolute", right: 8, top: 8 }}
        data-analytics-id="program-timeline-move-close"
      >
        <Close />
      </IconButton>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Sending isn’t affected. Who can see and edit it follows the new location.
          </Typography>
          <DestinationList
            query={query}
            onQuery={setQuery}
            rows={rows}
            chosen={destination?.id}
            onPick={setDestination}
            analyticsId="program-timeline-move-destination"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button
          onClick={onClose}
          disabled={moving}
          data-analytics-id="program-timeline-move-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={moving}
          disabled={!chosen}
          onClick={() => {
            if (chosen) void submit(chosen).catch(console.error);
          }}
          data-analytics-id="program-timeline-move-confirm"
        >
          Move
        </Button>
      </DialogActions>
    </>
  );
};

/**
 * Moves a Communication to another program or folder that the caller can edit, then says where it
 * went, with a button to move it back.
 */
export const MoveDialog: FC<MoveProps & { open: boolean }> = ({ open, onClose, ...props }) => {
  const mover = useMoveCommunication();
  const close = (): void => {
    if (!mover.moving) onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="move-title">
      <MoveForm {...props} {...mover} onClose={close} />
    </Dialog>
  );
};
